import { useState } from 'react'
import { useDocuments, useUploadDocument, useDeleteDocument } from '@/hooks/useDocuments'
import { getSignedUrl } from '@/lib/storage'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState } from '@/components/profile/EmptyState'
import { FileText, Plus, Trash2, CheckCircle2, Clock, XCircle } from 'lucide-react'
import { formatDate } from '@/lib/utils'

const DOC_TYPES: { value: string; label: string; authority?: string }[] = [
  { value: 'philsys', label: 'PhilSys (National ID)', authority: 'PSA' },
  { value: 'birth_certificate', label: 'Birth Certificate', authority: 'PSA' },
  { value: 'voters_id', label: "Voter's ID", authority: 'COMELEC' },
  { value: 'drivers_license', label: "Driver's License", authority: 'LTO' },
  { value: 'passport', label: 'Passport', authority: 'DFA' },
  { value: 'sss_id', label: 'SSS ID', authority: 'SSS' },
  { value: 'gsis_id', label: 'GSIS ID', authority: 'GSIS' },
  { value: 'umid', label: 'UMID', authority: 'SSS/GSIS' },
  { value: 'postal_id', label: 'Postal ID', authority: 'PHLPost' },
  { value: 'tin_id', label: 'TIN ID', authority: 'BIR' },
  { value: 'prc_id', label: 'PRC ID', authority: 'PRC' },
  { value: 'philhealth_id', label: 'PhilHealth ID', authority: 'PhilHealth' },
  { value: 'pwd_id', label: 'PWD ID', authority: 'DSWD' },
  { value: 'senior_citizen_id', label: 'Senior Citizen ID' },
  { value: 'solo_parent_id', label: 'Solo Parent ID', authority: 'DSWD' },
  { value: 'four_ps_id', label: '4Ps ID', authority: 'DSWD' },
  { value: 'barangay_id', label: 'Barangay ID' },
  { value: 'school_id', label: 'School ID' },
  { value: 'company_id', label: 'Company ID' },
  { value: 'marriage_certificate', label: 'Marriage Certificate', authority: 'PSA' },
  { value: 'cedula', label: 'Cedula' },
  { value: 'other', label: 'Other' },
]

function DocThumbnail({ storagePath }: { storagePath: string | null }) {
  const { data: url } = useQuery({
    queryKey: ['doc-thumb', storagePath],
    enabled: !!storagePath,
    queryFn: () => getSignedUrl('resident-documents', storagePath!, 3000),
    staleTime: 50 * 60 * 1000,
  })
  if (!storagePath) return <div className="flex h-16 w-16 items-center justify-center rounded bg-muted"><FileText className="h-6 w-6 text-muted-foreground" /></div>
  const isPdf = storagePath.endsWith('.pdf')
  if (isPdf) return <div className="flex h-16 w-16 items-center justify-center rounded bg-red-50"><FileText className="h-6 w-6 text-red-500" /></div>
  return url ? <img src={url} alt="" className="h-16 w-16 rounded object-cover" /> : <div className="h-16 w-16 animate-pulse rounded bg-muted" />
}

export function DocumentsPage() {
  const { data: docs = [], isLoading } = useDocuments()
  const upload = useUploadDocument()
  const deleteMut = useDeleteDocument()
  const [showUpload, setShowUpload] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [docType, setDocType] = useState('')
  const [docNumber, setDocNumber] = useState('')
  const [authority, setAuthority] = useState('')
  const [issuedDate, setIssuedDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [notes, setNotes] = useState('')
  const [uploadError, setUploadError] = useState<string | null>(null)

  const resetForm = () => { setShowUpload(false); setFile(null); setDocType(''); setDocNumber(''); setAuthority(''); setIssuedDate(''); setExpiryDate(''); setNotes(''); setUploadError(null) }

  const handleTypeChange = (v: string) => {
    setDocType(v)
    const match = DOC_TYPES.find((d) => d.value === v)
    if (match?.authority) setAuthority(match.authority)
  }

  const handleUpload = () => {
    if (!file || !docType) { setUploadError('File and document type are required'); return }
    if (file.size > 10 * 1024 * 1024) { setUploadError('Max 10MB'); return }
    setUploadError(null)
    upload.mutate({
      file, documentType: docType, documentNumber: docNumber || undefined,
      issuingAuthority: authority || undefined, issuedDate: issuedDate || undefined,
      expiryDate: expiryDate || undefined, notes: notes || undefined,
    }, { onSuccess: resetForm })
  }

  if (isLoading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />)}</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button size="sm" onClick={() => setShowUpload(true)}><Plus className="h-3 w-3" /> Upload Document</Button></div>

      {docs.length === 0 ? (
        <EmptyState icon={FileText} title="No documents yet" description="Upload your government IDs and certificates." actionLabel="Upload Document" onAction={() => setShowUpload(true)} />
      ) : docs.map((doc) => {
        const typeLabel = DOC_TYPES.find((d) => d.value === doc.document_type)?.label ?? doc.document_type
        return (
          <Card key={doc.id}>
            <CardContent className="flex items-start gap-3 pt-4">
              <DocThumbnail storagePath={doc.storage_path} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{typeLabel}</p>
                {doc.document_number && <p className="text-xs text-muted-foreground">#{doc.document_number}</p>}
                {doc.issuing_authority && <p className="text-xs text-muted-foreground">{doc.issuing_authority}</p>}
                {(doc.issued_date || doc.expiry_date) && (
                  <p className="text-xs text-muted-foreground">
                    {doc.issued_date && `Issued: ${formatDate(doc.issued_date)}`}
                    {doc.issued_date && doc.expiry_date && ' · '}
                    {doc.expiry_date && `Expires: ${formatDate(doc.expiry_date)}`}
                  </p>
                )}
                <div className="mt-1">
                  {doc.is_verified ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700"><CheckCircle2 className="h-3 w-3" /> Verified</span>
                  ) : doc.rejection_reason ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700"><XCircle className="h-3 w-3" /> Rejected: {doc.rejection_reason}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700"><Clock className="h-3 w-3" /> Pending verification</span>
                  )}
                </div>
              </div>
              <Button size="icon" variant="ghost" disabled={doc.is_verified} onClick={() => deleteMut.mutate({ id: doc.id, storagePath: doc.storage_path })}>
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </CardContent>
          </Card>
        )
      })}

      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={resetForm}>
          <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-3 font-semibold">Upload Document</h3>
            {uploadError && <p className="mb-2 text-xs text-red-600">{uploadError}</p>}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Document Type *</Label>
                <Select value={docType} onValueChange={handleTypeChange}><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1">
                <Label>File *</Label>
                <Input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                {file && <p className="text-xs text-muted-foreground">{file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
              </div>
              <div className="space-y-1"><Label>Document Number</Label><Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} /></div>
              <div className="space-y-1"><Label>Issuing Authority</Label><Input value={authority} onChange={(e) => setAuthority(e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label>Issued Date</Label><Input type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)} /></div>
                <div className="space-y-1"><Label>Expiry Date</Label><Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></div>
              </div>
              <div className="space-y-1"><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
              <Button className="w-full" onClick={handleUpload} disabled={upload.isPending || !file || !docType}>
                {upload.isPending ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
